import { useEffect, useState } from 'react';
import type { CandidateResponseRow } from '../../db/types';
import { partyVar } from '../../lib/buildCandidates';
import { useFeatureFlags } from '../../lib/useFeatureFlags';
import Header from '../Header';
import Footer from '../Footer';
import './KnowBeforeYouVote.css';

interface Props {
  slug: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const QUESTIONS: { field: keyof CandidateResponseRow; label: string }[] = [
  { field: 'why_vote', label: 'Why should people vote for you?' },
  { field: 'why_politics', label: 'Why did you decide to enter politics?' },
  { field: 'top_priorities', label: 'What are your top three priorities for Malta right now?' },
  { field: 'malta_vision', label: 'What kind of Malta do you want to build over the next five years?' },
  { field: 'district_issue', label: 'What problem do people in your district talk to you about most?' },
  { field: 'youth_vote', label: 'Why should young people vote for you?' },
  { field: 'admired_candidate', label: 'Which candidate, even outside your party, do you admire and why?' },
  { field: 'additional', label: 'Anything else you\'d like to add?' },
];

export default function CandidateProfilePage({ slug }: Props) {
  const flags = useFeatureFlags();
  const isHidden = flags.featured_candidate?.status === 'hidden';

  const [profile, setProfile] = useState<CandidateResponseRow | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'not-found' | 'error'>('loading');

  useEffect(() => {
    fetch(`/api/know-before-you-vote?slug=${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) { setStatus('not-found'); return null; }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ profile: CandidateResponseRow }>;
      })
      .then((d) => {
        if (!d) return;
        setProfile(d.profile);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, [slug]);

  const color = profile ? partyVar(profile.party) : 'var(--muted)';

  if (isHidden) {
    return (
      <>
        <Header />
        <div className="kbyv-profile-page">
          <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 12 }}>Not available</h1>
          <p>
            The Know Before You Vote section is currently unavailable.{' '}
            <a href="/" style={{ textDecoration: 'underline' }}>
              Go back to the homepage.
            </a>
          </p>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="kbyv-profile-page">
        <a href="/know-before-you-vote" className="kbyv-profile__back">
          ← Know Before You Vote
        </a>

        {status === 'loading' && (
          <p className="kbyv-profile__loading">Loading…</p>
        )}

        {status === 'not-found' && (
          <p className="kbyv-profile__not-found">
            This candidate profile doesn&apos;t exist or hasn&apos;t been published yet.
          </p>
        )}

        {status === 'error' && (
          <p className="kbyv-profile__error">
            Couldn&apos;t load this profile. Try refreshing the page.
          </p>
        )}

        {status === 'ready' && profile && (
          <>
            <div className="kbyv-profile__hero">
              <div
                className="kbyv-avatar kbyv-profile__avatar"
                style={{ background: color }}
                aria-hidden="true"
              >
                {initials(profile.candidate_name)}
              </div>
              <div className="kbyv-profile__info">
                <h1 className="kbyv-profile__name">{profile.candidate_name}</h1>
                <div className="kbyv-profile__tags">
                  <span className="kbyv-profile__party-tag" style={{ background: color }}>
                    {profile.party}
                  </span>
                  {profile.district && (
                    <span className="kbyv-profile__district-tag">
                      District {profile.district}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="kbyv-profile__questions">
              {QUESTIONS.map(({ field, label }) => {
                const answer = profile[field];
                if (!answer) return null;
                return (
                  <div key={field} className="kbyv-profile__q">
                    <p className="kbyv-profile__question">{label}</p>
                    <p className="kbyv-profile__answer">{String(answer)}</p>
                  </div>
                );
              })}
            </div>

            <p className="kbyv-profile__sponsored-footer">Sponsored content</p>
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
